// routes/_app._index.tsx
import CSREntry from 'src/clientRoutes/_app._index.csr'
import FullSSREntry from 'src/clientRoutes/_app._index.full-ssr'
import { getListArtworkFeedFromArgs } from 'src/components/ArtworkList/useArtworkFeed'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import {
  dynamicConfigContext,
  dynamicConfigMiddleware,
} from 'src/middleware/dynamic-config.middleware.server'
import {
  type ListArtworksQuery,
  type ListArtworksQueryVariables,
} from 'src/services/apollo/generated/graphql'
import {
  isOrderOptionPosSeq,
  OrderOption,
  translateOrderOptionForArtworkListQuery,
} from 'src/services/helpers/orderOptionParams'
import type {
  ArtworkGetRecommendArtworksResponse,
  ArtworkGetRecommendArtworksVisitorResponse,
} from 'src/services/orpc/generated'
import {
  ArtworkTypeFilterValue,
  getArtworkQueryArgsByArtworkFilter,
} from 'src/utils/artwork'
import dayjs from 'src/utils/dayjs'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { createHydratingClientLoader } from 'src/utils/ssr'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app._index'

const getClientModule = () => import('src/clientRoutes/_app._index')

/**
 * Pull the recommend feed for SSR. Picks the visitor or the authed
 * endpoint based on the request cookie, so a logged-in user sees the
 * *same* personalized grid the post-hydrate client fetch would
 * produce, instead of the visitor grid getting torn down + replaced
 * after `useEffect` resolves Apollo `me`.
 *
 * Why this matters: the previous implementation only ever called
 * `/v2/artwork/recommend-visitor`, so SSR HTML always shipped the
 * anonymous recommendation set. Once the client hydrate commit
 * resolved `useMyInfo()`, `useOrpcRecommendArtworks` flipped to the
 * authed branch (`/v2/artwork/recommend`), which returned a
 * completely different set of artworks — visible as a hard grid swap
 * for every signed-in user the first time they loaded `/`. Picking
 * the matching endpoint here, plus the SSR-safe
 * `useMayTokenBeValid()`-driven branch picker on the client (see
 * `useOrpcRecommendArtworks` rewrite), means SSR + first hydrate
 * commit read the same react-query cache key with the same data.
 *
 * Auth strategy: we forward the incoming `Cookie` header verbatim on
 * the authed call. The upstream `/v2/artwork/recommend` endpoint
 * accepts either a `user_token` cookie or an `Authorization: Bearer`
 * header; the cookie variant is what the browser sends today, so we
 * mirror it for parity. Anonymous callers go through the visitor
 * endpoint as before.
 */
async function fetchRecommendForSSR(
  first: number,
  cookieHeader: string | null,
  isLoggedIn: boolean,
) {
  const [
    { artworkGetRecommendArtworks, artworkGetRecommendArtworksVisitor },
    { createClient },
    { toArtworkPreviewFragment },
  ] = await Promise.all([
    import('src/services/orpc/generated/sdk.gen'),
    import('src/services/orpc/generated/client'),
    import('src/services/orpc/utils/typeAdapters'),
  ])

  const client = createClient({
    baseUrl: process.env.PIXAI_API_URL || 'https://api.pixai.art',
    // We forward the user's cookie manually via `headers` below.
    // `credentials: 'include'` is browser-only fetch semantics and has
    // no effect under Node's fetch; the explicit header is what
    // actually propagates the auth.
    credentials: 'omit',
  })

  const authHeaders: Record<string, string> = {}
  if (cookieHeader) authHeaders.Cookie = cookieHeader

  let data:
    | ArtworkGetRecommendArtworksVisitorResponse
    | ArtworkGetRecommendArtworksResponse
    | undefined

  try {
    if (isLoggedIn) {
      const result = await artworkGetRecommendArtworks({
        client,
        query: { first },
        headers: authHeaders,
        throwOnError: true,
      })
      data = result.data
    } else {
      // Forward the cookie header on the visitor branch too. The
      // `/v2/artwork/recommend-visitor` endpoint personalises its
      // response on the visitor's `device_id` / Amplitude tracking
      // cookies, so a SSR call without those cookies returns a
      // *different* anonymous-bucket set of artworks than the
      // browser sees post-hydrate (which sends the cookies natively).
      // That mismatch is the visible "SSR grid shows one set of
      // artworks → hydrate flips to a different set" on `/<lang>/`
      // for signed-out users. Mirroring the cookies on this server
      // call keeps SSR and post-hydrate cache-hit reads on the same
      // recommendation payload.
      const result = await artworkGetRecommendArtworksVisitor({
        client,
        query: { first },
        headers: authHeaders,
        throwOnError: true,
      })
      data = result.data
    }
  } catch {
    // Upstream blew up — bail out and let the client refetch.
    // Returning `null` skips the Apollo seed + react-query prefill;
    // the home grid then renders its existing skeleton until the
    // post-hydrate fetch finishes.
    return null
  }

  if (!data) return null

  const artworks = data.data.map(item => toArtworkPreviewFragment(item))

  return {
    // Connection-shaped representation consumed by `StatefulArtworkGrid`'s
    // `initialConnectionData` (and downstream Apollo `ListArtworks` cache
    // seeds for non-recommend paths).
    connection: {
      edges: artworks.map((artwork, index) => ({
        node: artwork,
        cursor: data.data[index]?.id,
      })),
      pageInfo: {
        hasNextPage: data.pageInfo.hasNextPage,
        hasPreviousPage: false,
        endCursor: data.pageInfo.endCursor,
        startCursor: data.data[0]?.id,
      },
      feedUuid: data.feedUuid,
    },
    // Raw oRPC response kept verbatim so the home route can prefill
    // react-query's infinite cache for `useOrpcRecommendArtworks`. This is
    // what stops the visible flash on hydration: without it CSR fires a
    // duplicate `/v2/artwork/recommend{-visitor?}` request and re-renders
    // the grid once react-query data arrives.
    rawResponse: data,
    isLoggedIn,
  }
}

function buildHomeApolloPayload(requestUrl: string) {
  const url = new URL(requestUrl)
  const tag = url.searchParams.get('tag') ?? undefined
  const filterType =
    (url.searchParams.get('type') as ArtworkTypeFilterValue | null) ??
    ArtworkTypeFilterValue.All
  const hidePrompts = url.searchParams.get('hidePrompts')
  const rawOrder = url.searchParams.get('sort') ?? OrderOption.recommended
  const order = translateOrderOptionForArtworkListQuery(rawOrder)
  const filterArgs = getArtworkQueryArgsByArtworkFilter({
    type: filterType,
    hidePrompts: hidePrompts === 'false' ? false : undefined,
  })

  // `order === recommended` matches the Recommendation branch that
  // `useArtworkFeed` would resolve to client-side (see `FeedCategory`).
  if (order === OrderOption.recommended) {
    const variables: ListArtworksQueryVariables = {
      isSafeSearch: true,
      tag,
      ...(filterArgs.type ? { type: filterArgs.type } : {}),
      ...(filterArgs.i2vPro ? { i2vPro: filterArgs.i2vPro } : {}),
      ...(filterArgs.extraArgs ?? {}),
      feed: 'recommend-v1',
      first: 24,
    }

    if (tag === 'challenge') {
      variables.tag = undefined
    }

    if (tag === 'all' || !tag) {
      variables.tag = undefined
    }

    return {
      mode: 'recommend' as const,
      tag,
      tagName: tag && tag !== 'challenge' ? tag : undefined,
      rawOrder,
      filterType,
      hidePromptsEnabled: hidePrompts === 'false',
      variables,
    }
  }

  const variables: ListArtworksQueryVariables = {
    isSafeSearch: true,
    tag,
    ...(filterArgs.type ? { type: filterArgs.type } : {}),
    ...(filterArgs.i2vPro ? { i2vPro: filterArgs.i2vPro } : {}),
    ...(filterArgs.extraArgs ?? {}),
  }

  if (isOrderOptionPosSeq(order)) {
    variables.first = 24
  } else {
    variables.last = 24
  }

  variables.feed = getListArtworkFeedFromArgs({
    order,
    type: variables.type,
    tag,
  })

  if (order === OrderOption.dailyRanking) {
    if (!variables.i2vPro) {
      variables.feed = 'daily_ranking_dedup'
    } else {
      const lastWeek = dayjs().subtract(1, 'week').startOf('day').toISOString()
      const thirtyMinutesAgo = dayjs()
        .subtract(30, 'minute')
        .startOf('minute')
        .toISOString()
      variables.orderBy = '-markInfo.likedCount'
      variables.time = {
        gt: lastWeek,
        lt: thirtyMinutesAgo,
      }
    }
  }

  if (order === OrderOption.mostLiked) {
    variables.orderBy = '-markInfo.likedCount'
  }

  if (tag === 'challenge') {
    variables.tag = undefined
  }

  if (tag === 'all' || !tag) {
    variables.tag = undefined
  }

  return {
    mode: 'list' as const,
    tag,
    tagName: tag && tag !== 'challenge' ? tag : undefined,
    rawOrder,
    filterType,
    hidePromptsEnabled: hidePrompts === 'false',
    variables,
  }
}

function getHomeOrderLabel(
  rawOrder: string,
  t: ReturnType<typeof getI18n>['t'],
): string {
  switch (rawOrder) {
    case OrderOption.trending:
      return t('common:order-option.random')
    case OrderOption.dailyRanking:
      return t('common:order-option.daily-ranking')
    case OrderOption.followingNews:
      return t('common:order-option.following-news')
    case OrderOption.newest:
      return t('common:order-option.newest')
    case OrderOption.recommended:
    default:
      return t('common:order-option.recommend')
  }
}

export const handle = {
  name: 'home',
  track: { scene: 'home' },
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

/**
 * Preload public dynamic configs used above the fold (banner carousel).
 * Scoped to the home route so other pages don't pay the extra HTTP cost.
 */
export const middleware = [dynamicConfigMiddleware]

/**
 * SSR loader. Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L529-L540
 * Note: pixai-server only sets title + description for home page, no image.
 */
export async function loader({ request, context }: Route.LoaderArgs) {
  const { t } = getI18n(context)
  const apolloRequest = buildHomeApolloPayload(request.url)
  const [{ fetchListArtworksForSSR }, { fetchTagForSSR }] = await Promise.all([
    import('src/utils/ssr-meta/graphql/artwork.server'),
    import('src/utils/ssr-meta/graphql/tag.server'),
  ])
  // Match the client's `useMayTokenBeValid()` probe so the SSR call
  // and the post-hydrate client cache read end up on the same
  // recommend endpoint — `readTokenHintFromCookie` reads the same
  // `user_token_expire_at` / `token-hint` cookies the client-side
  // `createRequestContextValue` consults.
  const { readTokenHintFromCookie } = await import('src/utils/request-context')
  const cookieHeader = request.headers.get('cookie')
  const ssrIsLoggedIn = readTokenHintFromCookie(cookieHeader)
  const [feedData, tagData] = await Promise.all([
    apolloRequest.mode === 'recommend'
      ? fetchRecommendForSSR(
          apolloRequest.variables.first ?? 24,
          cookieHeader,
          ssrIsLoggedIn,
        )
      : fetchListArtworksForSSR(apolloRequest.variables).then(
          (connection: ListArtworksQuery['artworks'] | null) =>
            connection
              ? { connection, rawResponse: null, isLoggedIn: ssrIsLoggedIn }
              : null,
        ),
    apolloRequest.tagName ? fetchTagForSSR(apolloRequest.tagName) : null,
  ])

  const defaultTitle = t('server:meta.home.title')
  const defaultDescription = t('server:meta.home.description')
  const tagDisplayName =
    tagData?.displayName || tagData?.name || apolloRequest.tagName || undefined
  const title = tagDisplayName
    ? t('common:meta.title.tag-artwork-list', {
        tag: `#${tagDisplayName}`,
      })
    : defaultTitle
  const description = tagDisplayName
    ? t('common:meta.description.tag', {
        tag: tagDisplayName,
        type: 'AI artworks',
        defaultValue: `Browse AI artworks tagged with #${tagDisplayName} on PixAI.`,
      })
    : defaultDescription
  const meta = staticMetaLoader({
    requestUrl: request.url,
    title,
    description,
  })

  // Preloaded public dynamic config (banner carousel etc.); see
  // dynamicConfigMiddleware in src/middleware/dynamic-config.middleware.server.
  const dynamicConfig = context.get(dynamicConfigContext)

  return {
    ...meta,
    dynamicConfig,
    homePreview: {
      initialConnectionData: feedData?.connection ?? undefined,
    },
    homeApolloRequest: {
      ...apolloRequest,
      // Keep both the connection (for Apollo seed in non-recommend feeds)
      // and the raw oRPC response (for react-query prefill in recommend
      // feeds). The route component picks the right one for each layer.
      feedConnection: feedData?.connection ?? null,
      feedRawResponse: feedData?.rawResponse ?? null,
      // Tells the route component which `useOrpcRecommendArtworks` cache
      // key to seed under (authed vs visitor). Matches the cookie probe
      // the home loader and the client `useMayTokenBeValid()` agree on.
      ssrIsLoggedIn,
      tagData,
    },
  }
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

// Cache the home loader payload in memory so back / forward navigation
// from a detail page (e.g. /artwork/:id -> /) doesn't stall on a
// `?_routes=routes/_app._index` round-trip. While that round-trip
// resolves React Router keeps the previous route DOM mounted, and
// because the browser scroll-restores to where the user left the
// detail page (often the bottom — `<ArtworkRecommendedUsers>` /
// `<ArtworkRelevantArtworksInline>`), the next paint visually looks
// like artwork-detail content "leaking" into the home transition.
// Caching the home loader for 5 minutes makes that swap synchronous,
// so artwork detail unmounts immediately and the home tree paints
// without an in-between frame.
//
// Trade-off: the home feed can be up to 5 minutes stale on back
// navigations. Likes / counters that bumped on the detail page won't
// immediately reflect on the cached grid. A hard reload (F5 /
// Ctrl+R) bypasses the cache.
const HOME_CLIENT_LOADER_CACHE_TTL_MS = 5 * 60 * 1000

export const clientLoader = createHydratingClientLoader<
  Awaited<ReturnType<typeof loader>>
>(getClientModule, { cacheTtlMs: HOME_CLIENT_LOADER_CACHE_TTL_MS })

/**
 * Dual-mode entry. `'off'` (CSR) path renders `HomePageWrapper`
 * directly under `<RootRuntime>`'s global Apollo / QueryClient with
 * no SSR seed; `'on'` (Full SSR) path mounts the route-scoped
 * Apollo + react-query bridge defined in
 * `clientRoutes/_app._index.full-ssr.tsx`.
 *
 * `_app.tsx` / `root.tsx` consult `handle.ssrShellExperiment` (see
 * `useResolvedSsrShell`) to decide whether to bypass `<RootRuntime>`
 * for this route, so `'off'` is equivalent to Stage 1 behaviour and
 * `'on'` flushes real first-paint HTML.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
